import { z } from 'zod';

// =============================================================================
// Block Type Constants
// =============================================================================

export const BLOCK_TYPE = {
  PUBLIC_HOLIDAY: 0,
  CONFERENCE: 1,
  PROVIDER_UNAVAILABLE: 2,
  STAFF_MEETING: 3,
  VACATION: 4,
} as const;

export const BLOCK_TYPE_LABELS: Record<number, string> = {
  [BLOCK_TYPE.PUBLIC_HOLIDAY]: 'Public Holiday',
  [BLOCK_TYPE.CONFERENCE]: 'Conference',
  [BLOCK_TYPE.PROVIDER_UNAVAILABLE]: 'Provider Unavailable',
  [BLOCK_TYPE.STAFF_MEETING]: 'Staff Meeting',
  [BLOCK_TYPE.VACATION]: 'Vacation',
};

// =============================================================================
// Response Schemas
// =============================================================================

export const scheduleBlockResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  providerId: z.string().uuid(),
  locationId: z.string().uuid().nullable(),
  blockType: z.number().int(),
  title: z.string(),
  description: z.string().nullable(),
  startTime: z.string(),
  endTime: z.string(),
  isFullDay: z.boolean(),
  rowStatus: z.number().int(),
  createdBy: z.string().uuid().nullable(),
  updatedBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  provider: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string(),
  }),
  location: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
    })
    .nullable(),
});
export type ScheduleBlockResponse = z.infer<typeof scheduleBlockResponseSchema>;

// =============================================================================
// Request Schemas
// =============================================================================

const BLOCK_TYPE_VALUES: readonly number[] = Object.values(BLOCK_TYPE);

/** One of {@link BLOCK_TYPE}; anything else is refused rather than stored. */
export const blockTypeSchema = z
  .number()
  .int()
  .refine((v) => BLOCK_TYPE_VALUES.includes(v), { message: 'Invalid block type' });

export const createScheduleBlockSchema = z.object({
  providerId: z.string().uuid(),
  locationId: z.string().uuid().optional(),
  blockType: blockTypeSchema,
  title: z.string().min(1, 'Title is required').max(200),
  description: z.string().max(1000).optional(),
  startTime: z.string().datetime(),
  endTime: z.string().datetime(),
  isFullDay: z.boolean().default(false),
  markAppointmentsRescheduling: z.boolean().default(false),
});
export type CreateScheduleBlockRequest = z.infer<typeof createScheduleBlockSchema>;

export const updateScheduleBlockSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(1000).nullable().optional(),
  blockType: blockTypeSchema.optional(),
});
export type UpdateScheduleBlockRequest = z.infer<typeof updateScheduleBlockSchema>;

// =============================================================================
// Check Overlaps
// =============================================================================

export const checkOverlapsRequestSchema = z.object({
  providerId: z.string().uuid(),
  startTime: z.string().datetime(),
  endTime: z.string().datetime(),
  locationId: z.string().uuid().optional(),
});
export type CheckOverlapsRequest = z.infer<typeof checkOverlapsRequestSchema>;

const overlappingAppointmentSchema = z.object({
  id: z.string().uuid(),
  startTime: z.string(),
  endTime: z.string(),
  status: z.number().int(),
  patient: z
    .object({
      id: z.string().uuid(),
      firstName: z.string(),
      lastName: z.string(),
    })
    .nullable(),
});
export type OverlappingAppointment = z.infer<typeof overlappingAppointmentSchema>;

export const checkOverlapsResponseSchema = z.object({
  overlappingAppointments: z.object({
    count: z.number().int(),
    appointments: z.array(overlappingAppointmentSchema),
  }),
  overlappingBlocks: z.array(
    z.object({
      id: z.string().uuid(),
      title: z.string(),
      startTime: z.string(),
      endTime: z.string(),
      blockType: z.number().int(),
    })
  ),
});
export type CheckOverlapsResponse = z.infer<typeof checkOverlapsResponseSchema>;

// =============================================================================
// Create Response (with affected count)
// =============================================================================

export const createScheduleBlockResponseSchema = z.object({
  block: z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    providerId: z.string().uuid(),
    locationId: z.string().uuid().nullable(),
    blockType: z.number().int(),
    title: z.string(),
    description: z.string().nullable(),
    startTime: z.string(),
    endTime: z.string(),
    isFullDay: z.boolean(),
    rowStatus: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
  affectedAppointmentCount: z.number().int(),
});
export type CreateScheduleBlockResponse = z.infer<typeof createScheduleBlockResponseSchema>;
